import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileSpreadsheet, BrainCircuit, LineChart, FileDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LANDING } from "@/constants/testIds/wizard";

const Feature = ({ icon: Icon, title, desc }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4 }}
    className="group rounded-xl border border-slate-200 bg-white p-6 transition-colors hover:border-blue-300"
  >
    <div className="mb-4 grid h-10 w-10 place-items-center rounded-md bg-blue-50 text-blue-700">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </div>
    <h3 className="font-heading text-base font-semibold tracking-tight text-slate-900">{title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
  </motion.div>
);

export default function Landing() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="mx-auto max-w-7xl px-6 pb-24 pt-16 lg:pt-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600" />
              Finch &amp; Partners · Strategic Advisoring
            </div>
            <h1 className="mt-6 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {t("hero_title_1")}
              <br />
              <span className="text-blue-700">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              {t("hero_desc")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/riclassifica">
                <Button
                  data-testid={LANDING.ctaUpload}
                  size="lg"
                  className="rounded-full bg-blue-700 px-6 py-6 text-base font-medium text-white hover:bg-blue-800"
                >
                  {t("cta_upload")}
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                </Button>
              </Link>
              <a
                href="#how"
                className="rounded-full border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
              >
                {t("cta_learn")}
              </a>
            </div>
            <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />
              {t("trust_line")}
            </div>
          </motion.div>

          {/* Right visual: mock balance sheet preview */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-blue-100 via-slate-50 to-white" />
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3 text-xs font-medium uppercase tracking-wider text-slate-500">
                  <span>Anteprima riclassificazione</span>
                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-700">AI ready</span>
                </div>
                <table className="mt-3 w-full font-mono text-[13px]">
                  <tbody>
                    {[
                      ["B.II", "Immobilizzazioni materiali", "€ 1.245.000"],
                      ["C.I", "Rimanenze", "€ 320.500"],
                      ["C.II", "Crediti v/clienti", "€ 780.120"],
                      ["C.IV", "Disponibilità liquide", "€ 145.200"],
                      ["D.FORN", "Debiti v/fornitori", "€ 421.660"],
                      ["A.RIC", "Ricavi vendite", "€ 3.480.000"],
                    ].map(([code, name, amt]) => (
                      <tr key={code} className="border-b border-slate-50 last:border-0">
                        <td className="w-14 py-2 text-xs text-blue-700">{code}</td>
                        <td className="py-2 text-slate-800">{name}</td>
                        <td className="py-2 text-right text-slate-900">{amt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {[
                    ["ROE", "12.4%", "good"],
                    ["Current", "1.8x", "good"],
                    ["Debt/E", "1.3x", "good"],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                      <div className="mt-0.5 font-mono text-sm font-medium text-slate-900">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Features */}
        <section id="features" className="mt-28">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-blue-700">Features</div>
              <h2 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900">
                {t("app_name")}
              </h2>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Feature icon={BrainCircuit} title={t("feature_1_title")} desc={t("feature_1_desc")} />
            <Feature icon={FileSpreadsheet} title={t("feature_2_title")} desc={t("feature_2_desc")} />
            <Feature icon={LineChart} title={t("feature_3_title")} desc={t("feature_3_desc")} />
            <Feature icon={FileDown} title={t("feature_4_title")} desc={t("feature_4_desc")} />
          </div>
        </section>

        {/* How */}
        <section id="how" className="mt-28">
          <div className="text-xs font-medium uppercase tracking-widest text-blue-700">
            {t("cta_learn")}
          </div>
          <h2 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900">
            3 step • {"< 60 secondi"}
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[t("how_step_1"), t("how_step_2"), t("how_step_3")].map((step, i) => (
              <div key={step} className="relative rounded-xl border border-slate-200 bg-white p-6">
                <div className="font-mono text-xs text-slate-400">STEP 0{i + 1}</div>
                <div className="mt-3 font-heading text-lg font-medium tracking-tight text-slate-900">
                  {step}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl border border-slate-200 bg-white p-8 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-heading text-xl font-medium tracking-tight text-slate-900">
                {t("cta_upload")}
              </div>
              <div className="mt-1 text-sm text-slate-600">{t("trust_line")}</div>
            </div>
            <Link to="/riclassifica">
              <Button className="rounded-full bg-blue-700 px-6 py-5 text-base text-white hover:bg-blue-800">
                {t("cta_upload")} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200">
        <Footer />
      </footer>
    </div>
  );
}
